import { router } from '@inertiajs/react'
import toast from 'react-hot-toast'
import { HiOutlineClipboardDocument, HiOutlinePencilSquare } from 'react-icons/hi2'
import { FaWhatsapp } from 'react-icons/fa'
import PriorityBadge, { type LeadPriority } from './PriorityBadge'
import { whatsappLink } from '../lib/whatsappLink'

export type LeadStage = 'prospect' | 'in_progress' | 'proposal_sent'

const STAGE_LABELS: Record<LeadStage, string> = {
  prospect: 'Prospect',
  in_progress: 'In Progress',
  proposal_sent: 'Proposal Sent',
}

const STAGES: LeadStage[] = ['prospect', 'in_progress', 'proposal_sent']

export type LeadHeaderData = {
  id: number
  name: string
  email: string
  phone: string | null
  priority: LeadPriority
  stage: LeadStage
  initials: string
}

type Props = {
  lead: LeadHeaderData
}

export default function LeadAccordionHeader({ lead }: Props) {
  const handleStageChange = (next: LeadStage) => {
    router.patch(
      `/leads/${lead.id}`,
      { stage: next },
      { preserveScroll: true, preserveState: true },
    )
  }

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(lead.email)
      toast.success('Email copied to clipboard')
    } catch {
      toast.error('Could not copy email')
    }
  }

  const whatsappHref = whatsappLink(lead.phone)

  return (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        {/* Aqua-blue ringed avatar with initials on bright-green background */}
        <div
          className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full border-[3px]"
          style={{
            backgroundColor: 'rgba(147, 213, 0, 0.20)',
            borderColor: '#00BED6',
          }}
        >
          <span className="font-display text-2xl font-medium tracking-tight text-ink">
            {lead.initials}
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-display text-lg font-medium tracking-tight text-gray-700">
            {lead.name}
          </p>
          <div className="flex items-center gap-2">
            <PriorityBadge priority={lead.priority} />
            <select
              value={lead.stage}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation()
                handleStageChange(e.target.value as LeadStage)
              }}
              className="cursor-pointer appearance-none rounded-md border bg-[right_0.5rem_center] bg-no-repeat px-3 py-1.5 pr-8 font-display text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-midnight-green"
              style={{
                backgroundColor: 'rgba(147, 213, 0, 0.20)',
                borderColor: '#93D500',
                color: '#111D1E',
                backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20' stroke='%23111D1E' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                backgroundSize: '14px',
              }}
            >
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {STAGE_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {whatsappHref ? (
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-3 rounded-md border border-gray-200 bg-white px-5 py-3 font-display text-base font-medium text-black/60 transition hover:bg-gray-50"
          >
            WhatsApp
            <FaWhatsapp className="h-5 w-5" aria-hidden="true" />
          </a>
        ) : (
          <span
            className="inline-flex cursor-not-allowed items-center gap-3 rounded-md border border-gray-200 bg-white px-5 py-3 font-display text-base font-medium text-black/30"
            title="No phone number"
          >
            WhatsApp
            <FaWhatsapp className="h-5 w-5" aria-hidden="true" />
          </span>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            copyEmail()
          }}
          className="inline-flex items-center gap-3 rounded-md border border-gray-200 bg-white px-5 py-3 font-display text-base font-medium text-black/60 transition hover:bg-gray-50"
        >
          Email
          <HiOutlineClipboardDocument className="h-5 w-5" aria-hidden="true" />
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            router.visit(`/leads/${lead.id}/edit`)
          }}
          className="inline-flex items-center gap-3 rounded-md border border-gray-200 bg-white p-3 font-display text-base font-medium text-black/60 transition hover:bg-gray-50"
          aria-label="Edit lead"
        >
          <HiOutlinePencilSquare className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
